import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, User, Building2, Phone, Landmark } from 'lucide-react';
import { employeeAPI, departmentMasterAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { filterVisibleEmployees } from '../lib/employeeVisibility';
import { CardSkeleton } from '../components/common/States';

const emptyForm = {
  first_name: '',
  middle_name: '',
  last_name: '',
  preferred_name: '',
  department: '',
  sub_department: '',
  designation: '',
  reporting_manager_id: '',
  second_level_manager_id: '',
  phone: '',
  personal_email: '',
  address: '',
  location: '',
  gender: '',
  marital_status: '',
  blood_group: '',
  nationality: '',
  employment_status: 'active',
  employment_type: '',
  work_mode: '',
  grade: '',
  cost_center: '',
  date_of_birth: '',
  date_of_joining: '',
  probation_end_date: '',
  pan_number: '',
  aadhaar_number: '',
  uan_number: '',
  pf_number: '',
  esi_number: '',
  bank_account_number: '',
  bank_name: '',
  ifsc_code: '',
};

const toInputDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const toNullable = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  return value;
};

const AdminEmployeeEditPage = ({ employeeId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [employees, setEmployees] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const selectedDepartment = useMemo(
    () => departmentOptions.find((row) => row.name === form.department),
    [departmentOptions, form.department]
  );
  const designationOptions = useMemo(() => {
    if (!selectedDepartment) return [];
    if (form.sub_department) {
      return selectedDepartment.sub_department_designations?.[form.sub_department] || [];
    }
    return selectedDepartment.designations || [];
  }, [selectedDepartment, form.sub_department]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [empRes, allRes, deptRes] = await Promise.all([
          employeeAPI.get(employeeId),
          employeeAPI.list(),
          departmentMasterAPI.listOptions().catch(() => ({ data: [] })),
        ]);
        const emp = empRes.data || {};
        setForm({
          ...emptyForm,
          ...emp,
          reporting_manager_id: emp.reporting_manager_id || '',
          second_level_manager_id: emp.second_level_manager_id || '',
          date_of_birth: toInputDate(emp.date_of_birth),
          date_of_joining: toInputDate(emp.date_of_joining),
          probation_end_date: toInputDate(emp.probation_end_date),
        });
        setEmployees(filterVisibleEmployees(allRes.data, user));
        setDepartmentOptions(deptRes.data || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load employee'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeId, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    try {
      setSubmitting(true);
      await employeeAPI.update(employeeId, {
        ...form,
        middle_name: toNullable(form.middle_name),
        preferred_name: toNullable(form.preferred_name),
        department: toNullable(form.department),
        sub_department: toNullable(form.sub_department),
        designation: toNullable(form.designation),
        phone: toNullable(form.phone),
        personal_email: toNullable(form.personal_email),
        address: toNullable(form.address),
        location: toNullable(form.location),
        gender: toNullable(form.gender),
        marital_status: toNullable(form.marital_status),
        blood_group: toNullable(form.blood_group),
        nationality: toNullable(form.nationality),
        employment_status: toNullable(form.employment_status),
        employment_type: toNullable(form.employment_type),
        work_mode: toNullable(form.work_mode),
        grade: toNullable(form.grade),
        cost_center: toNullable(form.cost_center),
        reporting_manager_id: toNullable(form.reporting_manager_id),
        second_level_manager_id: toNullable(form.second_level_manager_id),
        date_of_birth: toNullable(form.date_of_birth),
        date_of_joining: form.date_of_joining ? `${form.date_of_joining}T00:00:00` : null,
        probation_end_date: toNullable(form.probation_end_date),
        pan_number: toNullable(form.pan_number),
        aadhaar_number: toNullable(form.aadhaar_number),
        uan_number: toNullable(form.uan_number),
        pf_number: toNullable(form.pf_number),
        esi_number: toNullable(form.esi_number),
        bank_account_number: toNullable(form.bank_account_number),
        bank_name: toNullable(form.bank_name),
        ifsc_code: toNullable(form.ifsc_code),
      });
      toast.success('Employee updated successfully');
      navigate(`/admin/employees/${employeeId}/profile`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update employee'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-1 border-b border-border pb-4">
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Edit Employee</h2>
          <p className="text-sm text-muted-foreground">{employeeId}</p>
        </div>
        <CardSkeleton rows={6} />
      </div>
    );
  }

  return (
    <form className="space-y-6 pb-24" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-1 border-b border-border pb-4">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">Edit Employee</h2>
        <p className="text-sm text-muted-foreground">{employeeId}</p>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <User className="h-4 w-4 text-primary" />
            Personal Details
          </CardTitle>
          <p className="text-sm text-muted-foreground">Name, demographics and personal identifiers.</p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-6">
          <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
          <div><Label>Middle Name</Label><Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} /></div>
          <div><Label>Last Name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></div>
          <div><Label>Preferred Name</Label><Input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} /></div>
          <div><Label>Gender</Label><Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} /></div>
          <div><Label>Marital Status</Label><Input value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} /></div>
          <div><Label>Blood Group</Label><Input value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} /></div>
          <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></div>
          <div><Label>Date Of Birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Building2 className="h-4 w-4 text-primary" />
            Job &amp; Organization
          </CardTitle>
          <p className="text-sm text-muted-foreground">Department, reporting lines and employment details.</p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-6">
          <div>
            <Label>Department</Label>
            <Select value={form.department || '_none'} onValueChange={(v) => setForm({ ...form, department: v === '_none' ? '' : v, sub_department: '', designation: '' })}>
              <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {departmentOptions.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>{dept.name} {dept.location ? `(${dept.location})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sub Department</Label>
            <Select value={form.sub_department || '_none'} onValueChange={(v) => setForm({ ...form, sub_department: v === '_none' ? '' : v, designation: '' })}>
              <SelectTrigger><SelectValue placeholder="Select sub department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {(selectedDepartment?.sub_departments || []).map((sub) => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Designation</Label>
            <Select value={form.designation || '_none'} onValueChange={(v) => setForm({ ...form, designation: v === '_none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {designationOptions.map((designation) => (
                  <SelectItem key={designation} value={designation}>{designation}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div><Label>Employment Status</Label><Input value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })} /></div>
          <div><Label>Employment Type</Label><Input value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} /></div>
          <div><Label>Work Mode</Label><Input value={form.work_mode} onChange={(e) => setForm({ ...form, work_mode: e.target.value })} /></div>
          <div><Label>Grade</Label><Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} /></div>
          <div><Label>Cost Center</Label><Input value={form.cost_center} onChange={(e) => setForm({ ...form, cost_center: e.target.value })} /></div>
          <div><Label>Date Of Joining</Label><Input type="date" value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} /></div>
          <div><Label>Probation End Date</Label><Input type="date" value={form.probation_end_date} onChange={(e) => setForm({ ...form, probation_end_date: e.target.value })} /></div>
          <div>
            <Label>Reporting Manager</Label>
            <Select value={form.reporting_manager_id || '_none'} onValueChange={(v) => setForm({ ...form, reporting_manager_id: v === '_none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {employees.filter((e) => e.employee_id !== employeeId).map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} ({e.employee_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Second Level Manager</Label>
            <Select value={form.second_level_manager_id || '_none'} onValueChange={(v) => setForm({ ...form, second_level_manager_id: v === '_none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Select second level manager" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {employees.filter((e) => e.employee_id !== employeeId && e.employee_id !== form.reporting_manager_id).map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} ({e.employee_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Phone className="h-4 w-4 text-primary" />
            Contact
          </CardTitle>
          <p className="text-sm text-muted-foreground">Reachability and address information.</p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-6">
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Personal Email</Label><Input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Landmark className="h-4 w-4 text-primary" />
            Statutory &amp; Bank
          </CardTitle>
          <p className="text-sm text-muted-foreground">Compliance identifiers and payroll bank details.</p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-6">
          <div><Label>PAN Number</Label><Input value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value })} /></div>
          <div><Label>Aadhaar Number</Label><Input value={form.aadhaar_number} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })} /></div>
          <div><Label>UAN Number</Label><Input value={form.uan_number} onChange={(e) => setForm({ ...form, uan_number: e.target.value })} /></div>
          <div><Label>PF Number</Label><Input value={form.pf_number} onChange={(e) => setForm({ ...form, pf_number: e.target.value })} /></div>
          <div><Label>ESI Number</Label><Input value={form.esi_number} onChange={(e) => setForm({ ...form, esi_number: e.target.value })} /></div>
          <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
          <div><Label>Bank Account Number</Label><Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} /></div>
          <div><Label>IFSC Code</Label><Input value={form.ifsc_code} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value })} /></div>
        </CardContent>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex items-center justify-end gap-2 px-6 py-3">
          <Button type="button" variant="outline" onClick={() => navigate(`/admin/employees/${employeeId}/profile`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AdminEmployeeEditPage;
